
import Vue from "vue";
import { Account } from "@/interfaces/settings/account";
import { mapActions, mapGetters, mapMutations } from "vuex";
import {
  AUTH_LOADING,
  GET_PASSWORD,
  GET_USER_DETAILS,
  UPDATE_USER_DETAILS
} from "@/store/modules/auth/constants";
import { decrypt_password } from "@/utils/crypto";
import { get_password_validator_regex } from "@/utils/global";
import {
  email,
  maxLength,
  minLength,
  not,
  required,
  requiredIf,
  sameAs
} from "vuelidate/lib/validators";
import store from "@/store";
import { ROOT_NOTIFICATION } from "@/store/modules/root/constants";
import { get_color_by_name } from "@/utils/colors";

export default Vue.extend({
  name: "Account",
  data(): Account {
    return {
      name_icon: require("@/assets/icons/bold/user-octagon.svg"),
      email_icon: require("@/assets/icons/bold/sms.svg"),
      password_icon: require("@/assets/icons/bold/lock.svg"),
      show_password: false,
      edit_mode: false,
      edit_details_form: false,
      show_new_password: false,
      password: "**********",
      email_changed: false,
      avatar_uri: null,
      avatar_file: null,
      avatar_preview: null,
      show_avatar_error: false,
      avatar_error_message: "",
      update_details_form: {
        name: "",
        email: "",
        current_password: "",
        new_password: ""
      }
    };
  },
  validations() {
    return {
      update_details_form: {
        name: {
          required
        },
        email: {
          required,
          email: email
        },
        current_password: {
          required,
          min: minLength(8),
          max: maxLength(16),
          valid: (value: string) => get_password_validator_regex().test(value),
          change: function (value: string) {
            return (
              value === decrypt_password(store.getters[`auth/${GET_PASSWORD}`])
            );
          }
        },
        new_password: {
          required: requiredIf((value) => value.length > 1),
          min: minLength(8),
          max: maxLength(16),
          unique: not(sameAs("current_password")),
          valid: (value) =>
            value.length > 1 ? get_password_validator_regex().test(value) : true
        }
      }
    };
  },
  mounted() {
    this.update_details_form.name =
      this.get_user_details?.first_name +
      " " +
      this.get_user_details?.last_name;
    this.update_details_form.email = this.get_user_details.email;
    this.update_details_form.current_password = decrypt_password(
      this.get_user_details.password
    );
  },
  computed: {
    ...mapGetters("auth", {
      get_user_details: GET_USER_DETAILS,
      auth_loading: AUTH_LOADING
    }),
    nameModel: {
      get() {
        return this.$v.update_details_form.name?.$model || "";
      },
      set(value: string) {
        if (this.$v.update_details_form.name) {
          this.$v.update_details_form.name.$model = value;
        }
      }
    },
    emailModel: {
      get() {
        return this.$v.update_details_form.email?.$model || "";
      },
      set(value: string) {
        if (this.$v.update_details_form.email) {
          this.$v.update_details_form.email.$model = value;
        }
      }
    },
    currentPasswordModel: {
      get() {
        return this.$v.update_details_form.current_password?.$model || "";
      },
      set(value: string) {
        if (this.$v.update_details_form.current_password) {
          this.$v.update_details_form.current_password.$model = value;
        }
      }
    },
    newPasswordModel: {
      get() {
        return this.$v.update_details_form.new_password?.$model || "";
      },
      set(value: string) {
        if (this.$v.update_details_form.new_password) {
          this.$v.update_details_form.new_password.$model = value;
        }
      }
    }
  },
  methods: {
    ...mapMutations({
      root_notification: ROOT_NOTIFICATION
    }),
    ...mapActions("auth", {
      update_user_details: UPDATE_USER_DETAILS
    }),
    same_current_password(value: string): boolean {
      return value === decrypt_password(this.get_user_details.password);
    },
    transform_edit_icon(icon: HTMLElement): HTMLElement {
      const color = get_color_by_name("primary");
      for (let node of icon.children) node.setAttribute("stroke", color);
      return icon;
    },
    display_password() {
      this.show_password = !this.show_password;
      if (this.show_password)
        this.password = decrypt_password(this.get_user_details.password);
      else this.password = "**********";
    },
    trigger_file_input() {
      (this.$refs.avatar_input as HTMLInputElement).click();
    },
    handle_avatar_change(event: Event) {
      const input = event.target as HTMLInputElement;
      if (input.files && input.files[0]) {
        const file = input.files[0];

        // Validate file type
        if (!file.type.match("image.*")) {
          this.show_avatar_error = true;
          this.avatar_error_message = this.$t(
            "shared.settings.account.avatar-image-only"
          ).toString();
          return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
          this.show_avatar_error = true;
          this.avatar_error_message = this.$t(
            "shared.settings.account.avatar-size-error"
          ).toString();
          return;
        }

        this.avatar_file = file;
        this.show_avatar_error = false; // Clear error when avatar is selected
        this.avatar_error_message = "";

        // Create preview
        const reader = new FileReader();
        reader.onload = (e) => {
          this.avatar_preview = e.target?.result as string;
        };
        reader.onerror = () => {
          this.show_avatar_error = true;
          this.avatar_error_message = this.$t(
            "shared.settings.account.avatar-preview-error"
          ).toString();
        };
        reader.readAsDataURL(file);
      }
    },
    async update_details() {
      // Check if avatar is required
      if (!this.avatar_file && !this.get_user_details.avatar_uri) {
        this.show_avatar_error = true; // Show error message
        this.avatar_error_message = this.$t(
          "shared.settings.account.avatar-required"
        ).toString();
        return;
      }

      this.$v.$touch();
      if (!this.$v.$invalid) {
        const form = new FormData();
        const name = this.update_details_form.name;
        const split_name = name.split(" ");
        const first_name = split_name[0];
        const email = this.update_details_form.email;
        const current_name =
          this.get_user_details.first_name +
          " " +
          this.get_user_details.last_name;

        if (
          name === current_name &&
          email === this.get_user_details.email &&
          !this.update_details_form.new_password &&
          !this.avatar_file
        ) {
          this.root_notification(this.$t("shared.settings.account.no-update"));
          return;
        }
        let last_name: string | null = null;
        if (split_name.length > 1) {
          last_name = split_name.slice(1).join(" ");
          form.append("last_name", last_name as never);
        } else form.append("last_name", "");
        form.append("first_name", first_name);
        if (this.update_details_form.new_password) {
          form.append("password", this.update_details_form.new_password);
        }
        if (email !== this.get_user_details.email) {
          form.append("email", email);
          this.email_changed = true;
        }
        // Append avatar file if it exists
        if (this.avatar_file) {
          form.append("avatar", this.avatar_file);
        }
        if (this.get_user_details.avatar_uri) {
          form.append("avatar_uri", this.get_user_details.avatar_uri);
        }

        const result = await this.update_user_details(form);
        if (result) {
          this.root_notification("Profile Updated Successfully");
          this.update_details_form.current_password = this.update_details_form
            .new_password
            ? this.update_details_form.new_password
            : this.update_details_form.current_password;
          this.update_details_form.new_password = "";
          if (this.email_changed) {
            await this.$router.push({
              name: "verify-email"
            });
            this.email_changed = false;
          }
        }
      }
    }
  }
});
